/**
 * Products Store - Pinia store for product catalog and search
 * Manages product search (global and store-specific), details, fastline, and currency
 */

import { defineStore } from 'pinia'
import api from '@/services/api'
import axios from 'axios'
import type { Product, ProductDetails, ProductCriteria } from '@/types'
import type {
  ProductsState,
  SearchProductsParams,
  GetProductOptions,
  StoreCurrency
} from './types/product.types'

/**
 * Separate axios instance for external API calls (currency API)
 */
const externalHttp = axios.create({
  baseURL: 'https://lytningtomcat.chrilantech.com',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

/** In-memory cache for trending products */
let trendingCache: { data: Product[]; fetchedAt: number } | null = null
const TRENDING_CACHE_TTL = 30_000 // 30 seconds
const TRENDING_TIMEOUT = 5_000 // 5 seconds

const availabilityRank = (product: Product): number => {
  if (product.stockStatus === 'OUT_OF_STOCK' || product.stockStatus === 'DISCONTINUED') {
    return 1
  }
  if (typeof product.quantity === 'number' && product.quantity <= 0) {
    return 1
  }
  return 0
}

const sortAvailableFirst = (items: Product[]): Product[] => {
  return [...items]
    .map((product, index) => ({ product, index, rank: availabilityRank(product) }))
    .sort((a, b) => {
      if (a.rank !== b.rank) return a.rank - b.rank
      return a.index - b.index
    })
    .map((entry) => entry.product)
}

export const useProductsStore = defineStore('products', {
  state: (): ProductsState => ({
    list: [],
    detail: {},
    fastline: {},
    filters: {
      storeId: null,
      q: '',
      mainCategory: null,
      subCategory: null,
      subSubCategory: null,
      categoryId: null, // Keep for backward compatibility
      manufacturer: null,
      priceMin: null,
      priceMax: null,
      minRating: null,
      minReviewCount: null,
      inStockOnly: false,
      fastMovingOnly: false,
      sort: 'relevance',
      sortDirection: 'DESC',
      tags: [],
      page: 1,
      pageSize: 24
    },
    pagination: {
      page: 1,
      pageSize: 24,
      total: 0,
      pageCount: 0
    },
    loading: {
      search: false,
      detail: false,
      fastline: false,
      currency: false
    },
    error: {
      search: '',
      detail: '',
      fastline: '',
      currency: ''
    },
    currency: {}
  }),

  getters: {
    /**
     * Get all products in current search
     */
    products(state): Product[] {
      return state.list
    },

    /**
     * Get product by ID from cache
     */
    getProductById(state) {
      return (id: string): Product | null => {
        return state.detail[id] || null
      }
    },

    /**
     * Get fastline products for a store
     */
    getFastlineByStore(state) {
      return (storeId: string): Product[] => {
        return state.fastline[storeId] || []
      }
    },

    /**
     * Get current product filters
     */
    productFilters(state) {
      return state.filters
    },

    /**
     * Get product pagination
     */
    productPagination(state) {
      return state.pagination
    },

    /**
     * Get loading states
     */
    productsLoading(state) {
      return state.loading
    },

    /**
     * Get error states
     */
    productsError(state) {
      return state.error
    },

    /**
     * Get currency for a store
     */
    getStoreCurrency(state) {
      return (storeSlug: string): StoreCurrency => {
        return state.currency[storeSlug] || { Symbol: 'K', Rate: 1, Name: 'KWACHA' }
      }
    }
  },

  actions: {
    /**
     * Search for products
     * Supports both global search and store-specific search
     *
     * @param params - Search parameters
     * @returns Promise with list of products
     */
    async searchProducts(params: SearchProductsParams = {}): Promise<Product[]> {
      this.loading.search = true
      this.error.search = ''

    try {
      const merged = { ...this.filters, ...params }
      const isStore = !!merged.storeId

      // Map frontend parameters to backend ProductCriteria format
      const queryParams: ProductCriteria = {
        // Use backend field names to avoid relying on aliases
        searchText: merged.q || undefined,
        mainCategory: merged.mainCategory || merged.categoryId || undefined,
        subCategory: merged.subCategory || undefined,
        subSubCategory: merged.subSubCategory || undefined,
        manufacturer: merged.manufacturer || undefined,
        minPrice: merged.priceMin || undefined,
        maxPrice: merged.priceMax || undefined,
        minRating: merged.minRating || undefined,
        minReviewCount: merged.minReviewCount || undefined,
        inStockOnly: merged.inStockOnly || undefined,
        fastMovingOnly: merged.fastMovingOnly || undefined,
        sortBy: merged.sort as ProductCriteria['sortBy'],
        sortDirection: merged.sortDirection,
        tags: merged.tags?.length ? merged.tags : undefined,
        page: merged.page,
        pageSize: merged.pageSize,
        // keep size alias for safety
        size: merged.pageSize
      }

        // Call appropriate API endpoint (store-specific or global)
        // TODO: Add global product search
      const response = isStore
          ? await api.products.byStore(merged.storeId!, queryParams)
          : await api.products.search(queryParams)

        // PageResponse has 'data' property, not 'items'
        const items: Product[] = response.data || []
        const sortedItems = sortAvailableFirst(items)
        this.list = sortedItems
        this.filters = merged

        // Map PaginationInfo (currentPage, totalPages, totalItems) to internal Pagination (page, pageCount, total)
        this.pagination = {
          page: response.pagination?.currentPage || merged.page || 1,
          pageSize: response.pagination?.pageSize || merged.pageSize || 24,
          total: response.pagination?.totalItems || 0,
          pageCount: response.pagination?.totalPages || Math.ceil((response.pagination?.totalItems || 0) / (response.pagination?.pageSize || merged.pageSize || 24))
        }

        return sortedItems
      } catch (error: any) {
        this.error.search = error?.message || 'Failed to load products'
        return []
      } finally {
        this.loading.search = false
      }
    },

    /**
     * Get product details by ID
     * Results are cached to avoid redundant API calls
     *
     * @param id - Product ID
     * @param options - Options with force flag to bypass cache
     * @returns Promise with product or null
     */
  async getProduct(id: string, options: GetProductOptions = {}): Promise<ProductDetails | null> {
    const { force = false } = options

    // Return cached data if available and not forcing refresh
    if (!force && this.detail[id]) {
      return this.detail[id] as ProductDetails
    }

    this.loading.detail = true
    this.error.detail = ''

    try {
      const response = await api.products.get(id as any, undefined as any)
      const data = (response as any)?.data || (response as any)
      this.detail[id] = data
      return data as ProductDetails
    } catch (error: any) {
      this.error.detail = error?.message || 'Failed to load product'
      return null
    } finally {
      this.loading.detail = false
      }
    },

    /**
     * Get product details by store slug and product PLU
     * Results are cached to avoid redundant API calls
     *
     * @param slug - Store slug
     * @param plu - Product PLU (lookup code)
     * @param options - Options with force flag to bypass cache
     * @returns Promise with product or null
     */
    async getProductBySlugAndPlu(
      slug: string,
      plu: string,
      options: GetProductOptions = {}
    ): Promise<ProductDetails | null> {
      const { force = false } = options
      const cacheKey = `${slug}:${plu}`

      // Return cached data if available and not forcing refresh
      if (!force && this.detail[cacheKey]) {
        return this.detail[cacheKey]
      }

      this.loading.detail = true
      this.error.detail = ''

      try {
        const response = await api.products.get({ slug, plu })
        const data = (response as any)?.data || (response as any)
        this.detail[cacheKey] = data
        return data as ProductDetails
      } catch (error: any) {
        this.error.detail = error?.message || 'Failed to load product'
        return null
      } finally {
        this.loading.detail = false
      }
    },

    /**
     * Get fastline products for a store
     * Fastline products are featured/priority products
     * Results are cached per store
     *
     * @param storeId - Store ID
     * @param params - Optional parameters
     * @returns Promise with list of fastline products
     */
    async getFastline(storeId: string, params: Record<string, any> = {}): Promise<Product[]> {
      // Return cached if available
      if (this.fastline[storeId]) {
        return this.fastline[storeId]
      }

      this.loading.fastline = true
      this.error.fastline = ''

      try {
        const response = await api.products.fastline(storeId, params)
        // Handle both array response and PageResponse
        const items = Array.isArray(response) ? response : (response.data || [])
        const sortedItems = sortAvailableFirst(items)
        this.fastline[storeId] = sortedItems
        return sortedItems
      } catch (error: any) {
        this.error.fastline = error?.message || 'Failed to load fastline'
        return []
      } finally {
        this.loading.fastline = false
      }
    },

    /**
     * Update product filters
     */
    setProductFilters(partial: Partial<SearchProductsParams>) {
      this.filters = { ...this.filters, ...partial }
    },

    /**
     * Get store currency from external API
     * Results are cached per store
     *
     * @param storeSlug - Store slug
     * @returns Promise with currency data
     */
    async fetchStoreCurrency(storeSlug: string): Promise<StoreCurrency> {
      // Return cached if available
      if (this.currency[storeSlug]) {
        return this.currency[storeSlug]
      }

      this.loading.currency = true
      this.error.currency = ''

      try {
        // Make axios call to external currency API
        const response = await externalHttp.get(
          `/Franchise_OnlineServer/currency/${storeSlug}/getBaseCurrency`
        )
        const data: StoreCurrency = response.data
        this.currency[storeSlug] = data
        return data
      } catch (error: any) {
        this.error.currency = error?.message || 'Failed to load currency'
        // Return default currency if API fails
        const defaultCurrency: StoreCurrency = {
          Symbol: 'K',
          Rate: 1,
          Name: 'KWACHA',
          Base: true
        }
        this.currency[storeSlug] = defaultCurrency
        return defaultCurrency
      } finally {
        this.loading.currency = false
      }
    },

    /**
     * Fetch trending/popular products (global, no store filter)
     * Uses 30s in-memory cache and 5s timeout for graceful degradation
     */
    async fetchTrendingProducts(): Promise<Product[]> {
      // Return cached data if still fresh
      if (trendingCache && Date.now() - trendingCache.fetchedAt < TRENDING_CACHE_TTL) {
        return trendingCache.data
      }

      try {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), TRENDING_TIMEOUT)

        const response = await api.products.search({
          sortBy: 'rating',
          sortDirection: 'DESC',
          pageSize: 8,
          page: 1
        })

        clearTimeout(timeoutId)

        const items: Product[] = response.data || []
        const sorted = sortAvailableFirst(items)
        trendingCache = { data: sorted, fetchedAt: Date.now() }
        return sorted
      } catch {
        // Graceful failure: return cached data if available, otherwise empty
        return trendingCache?.data || []
      }
    },

    /**
     * Clear product detail cache
     */
    clearProductCache(id?: string) {
      if (id) {
        delete this.detail[id]
      } else {
        this.detail = {}
      }
    },

    /**
     * Clear fastline cache
     */
    clearFastlineCache(storeId?: string) {
      if (storeId) {
        delete this.fastline[storeId]
      } else {
        this.fastline = {}
      }
    }
  }
})
